import { Location } from '@angular/common';
import { ElementRef, Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, NavigationExtras, Router } from '@angular/router';
import { KalDialogConfig, KalDialogService, KalSnackbarService } from '@kalidea/kaligraphi';
import { Store } from '@ngxs/store';
import gql from 'graphql-tag';
import cloneDeep from 'lodash-es/cloneDeep';
import { BehaviorSubject, ConnectableObservable, EMPTY, from, Observable, of, Subject } from 'rxjs';
import { catchError, delay, filter, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { MenuInterface, routes } from 'src/app/_core/components/layout-nav/routes';
import { ActivityNavigationService, RouteActivityTypeFrom } from 'src/app/_core/services/activity-navigation.service';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { ReportPrintService } from 'src/app/_core/services/report-print.service';
import { Logger } from 'src/app/_core/tools/logger';
import {
    SharedNavigationManagerRemoveLastRouteAction,
    SharedNavigationManagerResetAction,
    SharedNavigationManagerSetRouteAction
} from 'src/app/_shared/stores/shared-navigation-manager/shared-navigation-manager.actions';
import {
    SharedNavigationManagerState
} from 'src/app/_shared/stores/shared-navigation-manager/shared-navigation-manager.state';
import { SelectMenuAction, SetActivatedMenusListAction } from 'src/app/_user/store/menu/menu.action';
import { MenuState } from 'src/app/_user/store/menu/menu.state';
import { UserState } from 'src/app/_user/store/user/user.state';
import { constants } from 'src/environments/constants';
import { BenefBenefPourAccesPortailModel, MutationUtiliseurPeutAccederAuMenuArgs } from 'src/graphql/generated-types';
import { GetPortalAccessBeneficiaryAction } from 'src/app/_user/store/user/user.action';
import {
    ConfirmDialogComponent,
    ConfirmDialogData
} from 'src/app/_shared/dialogs/confirm-dialog/confirm-dialog.component';

export class LayoutRoute {

    primary?: string | string[];

    content?: string | string[];

    focus?: string | string[];
}

export enum Layout {

    /**
     * 30 / 52
     */
    Focus = 'focus',

    /**
     * 41 / 41
     */
    Book = 'book',

    /**
     * 48 / 34
     */
    QuickWin = 'quick-win',

    /**
     * 58 / 24
     */
    Monitor = 'monitor',

    /**
     * 100 / 0
     */
    Full = 'full'
}

export type InternalNavigationExtra =
    NavigationExtras
    & { primaryRoute?: keyof LayoutRoute, navigateBack?: boolean, activityTypeFrom?: RouteActivityTypeFrom };

export type LayoutNavigationExtra = keyof LayoutRoute | InternalNavigationExtra;

@Injectable({
    providedIn: 'root'
})
export class LayoutService {

    private readonly isMobileSubject$ = new BehaviorSubject(false);

    private readonly logger = new Logger(this.constructor.name);

    private routeChangesSubject$ = new BehaviorSubject<LayoutRoute>({
        primary: [],
        content: [],
        focus: []
    });
    /**
     * store reference to navRef element
     */
    private navigationRefElementSubject = new BehaviorSubject<ElementRef>(null);

    /**
     * Cache des droits d'accès aux menus par code menu
     * - TTL de 1 heure pour limiter l'impact des modifications de profil
     * - Vidé lors du changement de délégation ou de la déconnexion
     */
    private readonly menuAccessCache = new Map<string, { value$: Observable<boolean>, timestamp: number }>();
    private readonly MENU_ACCESS_CACHE_TTL_MS = 60 * 60 * 1000; // 1 heure

    constructor(private readonly router: Router,
                private readonly location: Location,
                private readonly route: ActivatedRoute,
                private readonly store: Store,
                private readonly dialogService: KalDialogService,
                private readonly graphqlService: GraphqlService,
                private readonly snackbarService: KalSnackbarService,
                private readonly activityNavigationService: ActivityNavigationService,
                private readonly reportPrintService: ReportPrintService) {
        const currentRoutes = {};

        this.router.events
            .pipe(
                filter(element => element instanceof NavigationEnd),
                tap((event: NavigationEnd) => {
                    ['primary', 'content', 'focus'].map(
                        element => {
                            currentRoutes[element] = this.getRoutesByUrl(element, event.urlAfterRedirects);
                        }
                    );
                    this.routeChangesSubject$.next(cloneDeep(currentRoutes));
                })
            ).subscribe();
    }

    private _layoutWrapperLoading = new BehaviorSubject(false);

    set layoutWrapperLoading(value: boolean) {
        this._layoutWrapperLoading.next(value);
    }

    get isUserConnected(): boolean {
        const userContext = this.store.selectSnapshot(UserState.userContext);
        return !!(userContext && userContext.CurrentCE);
    }

    get isMobile$(): Observable<boolean> {
        return this.isMobileSubject$.asObservable().pipe(shareReplay());
    }

    get isMobile(): boolean {
        return this.isMobileSubject$.value;
    }

    set isMobile(isMobile: boolean) {
        this.isMobileSubject$.next(isMobile);
    }

    get routeChanges$(): Observable<LayoutRoute> {
        return this.routeChangesSubject$.asObservable();
    }

    get routes(): LayoutRoute {
        return this.routeChangesSubject$.getValue();
    }

    get navigationRefElement(): ElementRef {
        return this.navigationRefElementSubject.value;
    }

    set navigationRefElement(navRef: ElementRef) {
        this.navigationRefElementSubject.next(navRef);
    }

    get moduleActive(): { primary: string, content: string, focus: string } {
        const [primary, content, focus] = ['primary', 'content', 'focus'].map(
            e => {
                return this.getSegment(this.getRoutesByUrl(e, this.currentRoute), 1);
            }
        );

        return {primary, content, focus};
    }

    get currentRoute(): string {
        return this.location.path().split('?')[0];
    }

    get isHomeActive(): boolean {
        return this.moduleActive.content === 'home' || this.route.snapshot.queryParams.page === 'home';
    }

    get layoutWrapperLoading$(): Observable<boolean> {
        return this._layoutWrapperLoading.asObservable();
    }

    static isFromOutlet(currentRoute: ActivatedRouteSnapshot, outlet: string = 'content'): boolean {
        let parent = currentRoute;
        let hasContentOutlet = false;

        while (parent.parent) {
            hasContentOutlet = parent.outlet === outlet;
            parent = parent.parent;
        }

        return hasContentOutlet;
    }

    getMenuFromCode(menuCode: string, activityId: number): number {
        const getMenuParam = (menu): number => +menu.MenuPage?.ListeParametres?.MenuPageParamDTO?.find(menuParam => menuParam?.Libelle === 'ACT_ID')?.Valeur;

        return this.store.selectSnapshot(MenuState.menusList)
            .find(menu => getMenuParam(menu) === activityId && menu.MenuPage?.Classe === menuCode)?.Id;
    }

    navigateToHome(): Observable<any> {
        return this.store.dispatch(new SharedNavigationManagerResetAction()).pipe(
            switchMap(() => this.store.dispatch(new SetActivatedMenusListAction())),
            switchMap(() => this.store.dispatch(new SelectMenuAction())),
            tap(() => this.navigate({
                content: 'home',
                focus: ['home', 'informations'],
            }, {primaryRoute: 'content', navigateBack: true}))
        );
    }

    navigateToLogin(): Observable<any> {
        this.reportPrintService.clearCourrierIdCache();
        this.clearMenuAccessCache();
        return this.store.dispatch(new SharedNavigationManagerResetAction()).pipe(
            tap(() => this.navigate({
                content: 'user/login',
                focus: 'user/informations'
            }, 'content'))
        );
    }

    navigate(routes: LayoutRoute, extra: LayoutNavigationExtra, menuCode: string = null): Observable<boolean> {
        if (!menuCode) {
            menuCode = this.getMenuByUrl(routes)?.code;
        }
        const navigation$ = new Subject<boolean>();

        const completeNavigation = (value, params = {hasError: true}): void => {
            navigation$[params.hasError ? 'error' : 'next'](value);
            navigation$.complete();
        };

        this.checkUserAccess$(menuCode).pipe(
            delay(1),
            take(1),
            switchMap(granted => {
                if (!granted) {
                    completeNavigation(granted);
                    this.snackbarService.open({title: 'Votre profil sur la délégation courante ne permet pas d\'accéder à cette page.'});
                    return EMPTY;
                }

                return of(null);
            }),
            switchMap(() => {
                if (typeof extra !== 'string' && !!extra.activityTypeFrom) {
                    return this.activityNavigation(routes, extra);
                }
                return this.standardNavigation(routes, extra);
            }),
            tap((result) => {
                completeNavigation(result, {hasError: false});
            }),
            catchError(err => {
                completeNavigation(err);
                return of(null);
            })
        ).subscribe();

        return navigation$.asObservable();
    }

    /**
     * Change la route 'home/notification/list' en ['home', 'notification', 'list']
     */
    routeToArray(route: string | string[]): string[] {
        if (!route) {
            return [];
        }
        return Array.isArray(route) ? route : route.split('/');
    }

    getSegment(path: string[], segmentLength = 1): string {
        let route = '';

        if (path) {
            for (let i = 0; i < segmentLength; i++) {
                route += (i !== 0 && path[i] ? '/' : '') + (path[i] || '');
            }
        }

        return route;
    }

    navigateBack(defaultOptions?: [LayoutRoute, LayoutNavigationExtra], overrideExtra?: LayoutNavigationExtra): Observable<boolean> {
        // Si aucune route n'est dans le state alors on recupère la route par défaut
        const previousRoute = cloneDeep(this.store.selectSnapshot(SharedNavigationManagerState.previousRoute)) || (defaultOptions ? [defaultOptions[0], defaultOptions[1]] : null);
        if (!previousRoute) {
            return this.navigateToHome();
        }

        // Récupère les extra
        const getExtra = (routeOptions: LayoutNavigationExtra, routeOverridedExtra?: LayoutNavigationExtra): InternalNavigationExtra => {
            const overridedExtra = !!routeOverridedExtra
                ? getExtra(routeOverridedExtra)
                : {};

            const baseExtra = {
                ...{navigateBack: true},
                ...overridedExtra
            };

            if (typeof routeOptions === 'string') {
                return {
                    primaryRoute: routeOptions,
                    ...baseExtra
                };
            }

            return {
                ...routeOptions,
                ...baseExtra
            };
        };

        const route = previousRoute[0];
        const options = getExtra(previousRoute[1], overrideExtra);

        return this.store.dispatch(new SharedNavigationManagerRemoveLastRouteAction()).pipe(
            switchMap(() => this.navigate(route, options))
        );
    }

    openPortalDialog$(): Observable<ConfirmDialogData> {
        return this.store.dispatch(new GetPortalAccessBeneficiaryAction()).pipe(
            switchMap(() => {
                const benefPortailAccess = this.store.selectSnapshot(UserState.benefPortailAccess);
                let body = `Vous n'avez pas accès au portail`;
                const urlPortail = this.getUrlPortail();

                if (urlPortail) {
                    body = this.messagePortalAccess(benefPortailAccess);
                }

                const shouldNavigatePortal = !!urlPortail && !!benefPortailAccess?.PeutAccederAuPortail;

                const data: ConfirmDialogData = {
                    body,
                    cancelLabel: shouldNavigatePortal ? 'Annuler' : null,
                    submitLabel: shouldNavigatePortal ? 'Accéder au site Internet' : 'Ok'
                };
                const config = new KalDialogConfig({
                    data,
                    hasBackdrop: true,
                    backdropClass: 'cdk-overlay-dark-backdrop',
                    title: 'Accéder au site internet',
                });

                return this.dialogService.open(ConfirmDialogComponent, config).afterClosed.pipe(
                    tap((result: ConfirmDialogData) => {
                        if (!!result?.accepted && shouldNavigatePortal) {
                            this.navigateToPortal();
                        }
                    })
                );
            })
        );
    }

    checkUserAccess$(menuCode: string): Observable<boolean> {
        if (!menuCode) {
            return of(true);
        }

        const now = Date.now();
        const cached = this.menuAccessCache.get(menuCode);

        // Retourner le résultat caché si disponible et non expiré
        if (cached && (now - cached.timestamp) < this.MENU_ACCESS_CACHE_TTL_MS) {
            return cached.value$;
        }

        // Créer l'observable avec shareReplay pour partager le résultat
        const access$ = this.graphqlService.mutateOne<'UtiliseurPeutAccederAuMenu', MutationUtiliseurPeutAccederAuMenuArgs>({
            mutation: gql`
                    mutation UtiliseurPeutAccederAuMenu(
                      $contexte: ContextInfosInput,
                      $NomGestionMenuPge: String,
                      $CodeVersionLogiciel: String
                    ) {
                      UtiliseurPeutAccederAuMenu(
                        contexte: $contexte,
                        NomGestionMenuPge: $NomGestionMenuPge,
                        CodeVersionLogiciel: $CodeVersionLogiciel
                      )
                    }`,
            variables: {
                NomGestionMenuPge: menuCode,
                CodeVersionLogiciel: constants.codeVersionLogiciel
            }
        }).pipe(
            shareReplay(1) // Partage le résultat entre tous les souscripteurs sans relancer l'appel
        );

        this.menuAccessCache.set(menuCode, { value$: access$, timestamp: now });
        return access$;
    }

    /**
     * Vide le cache des droits d'accès aux menus
     * Appelé lors du changement de délégation ou de la déconnexion
     */
    clearMenuAccessCache(): void {
        this.menuAccessCache.clear();
    }

    private standardNavigation(routes: LayoutRoute, extra: LayoutNavigationExtra): Observable<boolean> {
        this.logger.trace(routes)();

        const commands = [{outlets: {}}];

        // Si la navigation a été faites par la directive navigateBack
        // Ce qui permet d'enlever la route de l'historique des routes dans le state
        let navigateBack = false;

        Object.keys(routes).forEach(
            route => {
                commands[0].outlets[route] = this.routeToArray(routes[route]);
            }
        );

        let primaryRoute: keyof LayoutRoute;

        if (extra) {
            if (typeof extra === 'string') {
                primaryRoute = extra;
                extra = null;
            } else {
                primaryRoute = extra.primaryRoute;
                navigateBack = extra.navigateBack;

                delete extra.primaryRoute;
                delete extra.navigateBack;
            }

            const route = routes[primaryRoute];
            if (primaryRoute && route) {
                // @ts-ignore
                commands[0].outlets.primary = this.routeToArray(route);
            }
        }

        if (!navigateBack) {
            const currentRoutes = {
                ...this.routes,
                ...routes
            };

            delete currentRoutes.primary;

            this.store.dispatch(new SharedNavigationManagerSetRouteAction([currentRoutes, {primaryRoute, ...(extra as NavigationExtras)}]));
        }

        return from(this.router.navigate(commands, extra as NavigationExtras || {}));
    }

    private activityNavigation(routes: LayoutRoute, extra: InternalNavigationExtra): Observable<boolean> {
        const navigation$ = this.activityNavigationService
            .constructRoutes$(extra.activityTypeFrom, routes)
            .pipe(
                switchMap(newRoutes => {
                    delete extra.activityTypeFrom;
                    return this.standardNavigation(newRoutes, extra);
                })
            );

        // Permet de ne pas avoir a souscrire à la navigation dans les composants
        const connectableNavigation$ = new ConnectableObservable(navigation$, () => new Subject());
        connectableNavigation$.connect();
        connectableNavigation$.subscribe();

        return connectableNavigation$;
    }

    private getRoutesByUrl(layoutRoute, url: string): string[] {
        const route = this.router.parseUrl(url).root.children[layoutRoute];
        return route ? route.segments.map(e => e.path) : [];
    }

    private getMenuByUrl(layoutRoute: LayoutRoute): MenuInterface {
        const route = Array.isArray(layoutRoute.content)
            ? layoutRoute.content.join('/')
            : layoutRoute.content;
        return routes.find(r => r.route[0].content === route);
    }

    private getUrlPortail(): string {
        const urlPortail = this.store.selectSnapshot(UserState.urlPortail);
        const {NumSession} = this.store.selectSnapshot(UserState.userContext);

        return urlPortail + '/?Preview=' + NumSession;
    }

    private messagePortalAccess(benefPortalAccess: BenefBenefPourAccesPortailModel): string {
        if (!benefPortalAccess) {
            return `Vous ne pouvez pas vous connecter sur le Portail car votre compte utilisateur n'est pas lié à un bénéficiaire.`;
        } else if (benefPortalAccess.PeutAccederAuPortail) {
            return `Attention, toutes les actions effectuées sur le bénéficiaire lié à votre compte
                     utilisateur ${benefPortalAccess.Prenom || ''} ${benefPortalAccess.Nom || ''} seront définitives.`;
        }

        return `Le bénéficiaire lié à votre compte utilisateur est hors droit`;
    }

    private navigateToPortal(): void {
        const urlPortail = this.getUrlPortail();

        if (urlPortail) {
            window.open(urlPortail);
        }
    }

}
